import React, { useState, useEffect } from 'react';
import { apiFetch } from '@/lib/apiFetch';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Bot, Save, Check, Sparkles } from 'lucide-react';

const BOT_TYPES = [
  { value: 'lead_qualifier', label: 'Lead Qualifier', desc: 'Qualify incoming leads with smart questions' },
  { value: 'appointment_setter', label: 'Appointment Setter', desc: 'Confirm interest and schedule calls or appointments' },
  { value: 'outreach_bot', label: 'Outreach Bot', desc: 'Re-engage cold leads with personalised follow-ups' },
  { value: 'general', label: 'General Assistant', desc: 'Friendly, professional all-purpose AI for any conversation' },
  { value: 'customer_support', label: 'Customer Support', desc: 'Resolve issues empathetically and escalate when needed' },
  { value: 'sales', label: 'Sales Assistant', desc: 'Consultative selling — understand needs, recommend the right fit' },
  { value: 'appointment_booking', label: 'Appointment Booking', desc: 'Schedule meetings using your agency\'s available times' },
];

export default function BotConfig() {
  const [botType, setBotType] = useState('');
  const [customPrompt, setCustomPrompt] = useState('');
  const [templates, setTemplates] = useState({});
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchConfig();
    fetchTemplates();
  }, []);

  const fetchConfig = async () => {
    try {
      const { data } = await apiFetch('/api/bot/config');
      setConfig(data);
      if (data.bot_type) setBotType(data.bot_type);
      if (data.custom_prompt) setCustomPrompt(data.custom_prompt);
    } catch {
      // ignore
    }
  };

  const fetchTemplates = async () => {
    try {
      const { data } = await apiFetch('/api/bot/templates');
      setTemplates(data);
    } catch {
      // ignore
    }
  };

  const handleBotTypeChange = (val) => {
    setBotType(val);
    // Auto-fill template if prompt is empty or matches a template
    const currentIsTemplate = Object.values(templates).includes(customPrompt);
    if (!customPrompt || currentIsTemplate) {
      setCustomPrompt(templates[val] || '');
    }
  };

  const handleSave = async () => {
    if (!botType) {
      setError('Select a bot type');
      return;
    }
    setError('');
    setSaving(true);
    setSaved(false);
    try {
      await apiFetch('/api/bot/config', {
        method: 'POST',
        body: { bot_type: botType, custom_prompt: customPrompt },
      });
      setSaved(true);
      await fetchConfig();
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const loadTemplate = () => {
    if (botType && templates[botType]) {
      setCustomPrompt(templates[botType]);
    }
  };

  return (
    <div className="border border-[#27272A] rounded-lg bg-[#1A1A1A] p-5 transition-all duration-200 hover:border-[#7B2FFF]/30">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Bot className="w-4 h-4 text-[#7B2FFF]" />
          <h3 className="text-sm font-semibold text-[#EDEDF0]" style={{ fontFamily: "'Unbounded', sans-serif" }}>
            Bot Configuration
          </h3>
        </div>
        {config?.active && (
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full bg-emerald-400" />
            <span className="text-xs text-emerald-400 font-medium">Active</span>
          </div>
        )}
      </div>

      <div className="space-y-4">
        {/* Bot Type Selector */}
        <div className="space-y-1.5">
          <Label className="text-xs text-[#A1A1AA]">Bot Type</Label>
          <Select value={botType} onValueChange={handleBotTypeChange}>
            <SelectTrigger
              data-testid="bot-type-selector"
              className="bg-[#111111] border-[#27272A] text-[#EDEDF0] focus:border-[#7B2FFF] focus:ring-[#7B2FFF] rounded-md h-10"
            >
              <SelectValue placeholder="Select bot type..." />
            </SelectTrigger>
            <SelectContent className="bg-[#1A1A1A] border-[#27272A] text-[#EDEDF0]">
              {BOT_TYPES.map(({ value, label, desc }) => (
                <SelectItem
                  key={value}
                  value={value}
                  data-testid={`bot-type-${value}`}
                  className="focus:bg-[#7B2FFF]/10 focus:text-[#EDEDF0]"
                >
                  <div>
                    <span className="font-medium">{label}</span>
                    <span className="text-xs text-[#71717A] ml-2">{desc}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* AI Prompt Editor */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label className="text-xs text-[#A1A1AA]">AI Prompt</Label>
            {botType && templates[botType] && (
              <button
                data-testid="load-template-button"
                onClick={loadTemplate}
                className="flex items-center gap-1 text-xs text-[#7B2FFF] hover:text-[#9353FF] transition-colors"
              >
                <Sparkles className="w-3 h-3" />
                Load template
              </button>
            )}
          </div>
          <div className="terminal-bg rounded-md border border-[#27272A] overflow-hidden">
            <Textarea
              data-testid="ai-prompt-editor"
              value={customPrompt}
              onChange={(e) => setCustomPrompt(e.target.value)}
              placeholder="Enter your AI prompt... e.g. 'You are a friendly receptionist for a plumbing company. Qualify leads and book jobs.'"
              rows={5}
              className="bg-transparent border-0 text-[#EDEDF0] placeholder:text-[#71717A] font-mono text-sm focus-visible:ring-0 focus-visible:ring-offset-0 resize-none relative z-10"
            />
          </div>
        </div>

        {error && <p className="text-xs text-red-400">{error}</p>}

        <Button
          data-testid="bot-config-save-button"
          onClick={handleSave}
          disabled={saving || !botType}
          className="w-full bg-[#7B2FFF] hover:bg-[#9353FF] text-white font-medium rounded-md h-9 text-sm transition-all duration-200 hover:shadow-[0_0_16px_rgba(123,47,255,0.25)] active:scale-[0.98] disabled:opacity-40"
        >
          {saving ? (
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : saved ? (
            <span className="flex items-center gap-1.5"><Check className="w-4 h-4" /> Configuration Saved</span>
          ) : (
            <span className="flex items-center gap-1.5"><Save className="w-4 h-4" /> Save Configuration</span>
          )}
        </Button>
      </div>
    </div>
  );
}
